<template>
  <div class="sb-checkbox" :class="componentClasses" :data-intro-js-hint="id">
    <label class="sb-checkbox__inner" :data-testid="`${dataTestid}-label`">
      <input
        :id="id || uid"
        v-bind="$attrs"
        :data-testid="dataTestid"
        :checked="modelValue"
        :indeterminate.prop="indeterminate"
        class="sb-checkbox__native"
        type="checkbox"
        :value="nativeValue"
        :required="required"
        :aria-required="required"
        :aria-disabled="disabled"
        :aria-label="hideLabel ? label : undefined"
        :name="name"
        :aria-describedby="description ? `sb-checkbox-hint-${uid}` : undefined"
        @click="handleClick"
        @blur="handleBlur"
      />

      <div class="sb-checkbox__input">
        <svg class="sb-checkbox__svg" focusable="false" viewBox="0 0 16 16">
          <path
            class="sb-checkbox__truthy"
            fill="none"
            d="M11.611 5.27l.008.009c.37.374.37.977 0 1.352l-4.004 4.054a.934.934 0 01-.496.264l-.11.013-.111.001a.935.935 0 01-.618-.278L4.277 8.658a.962.962 0 010-1.352.938.938 0 011.327-.008l.008.008 1.335 1.351 3.337-3.378a.938.938 0 011.327-.008z"
          />
          <rect
            class="sb-checkbox__indeterminate"
            x="4"
            y="7"
            width="8"
            height="2"
          />
        </svg>
      </div>
      <span
        v-if="label && !hideLabel && !$slots.label"
        class="sb-checkbox__label"
        :class="labelClass"
      >
        {{ label }}
      </span>
      <!-- @slot allows you to customize the label content of the SbCheckbox component using custom HTML or Vue components. -->
      <slot
        name="label"
        class="sb-checkbox__label"
        :class="labelClass"
        :for="id || uid"
      ></slot>
    </label>
    <span
      v-if="description && !hideLabel"
      :id="`sb-checkbox-hint-${uid}`"
      class="sb-checkbox__description"
    >
      {{ description }}
    </span>
  </div>
</template>

<script lang="ts" setup>
import { computed, useAttrs, useId } from 'vue'

defineOptions({
  inheritAttrs: false,
})

type CheckboxProps = {
  /**
   * The label of the checkbox
   */
  label: string

  /**
   * To disable the checkbox. But, it will still be visible by the screen readers
   */
  disabled?: boolean

  /**
   * To hide the label as a text, but still binding the label to the checkbox
   */
  hideLabel?: boolean

  /**
   * The checkbox ID
   */
  id?: string

  /**
   * The test id to be used in e2e tests
   */
  dataTestid?: string

  /**
   * To define the checkbox as indeterminate
   */
  indeterminate?: boolean

  /**
   * Controls the checked state of the checkbox
   */
  modelValue?: boolean

  /**
   * Name of the checkbox
   */
  name?: string

  /**
   * The value of the checkbox
   */
  nativeValue?: string | number | boolean

  /**
   * To define the checkbox as required. Useful for form validation
   */
  required?: boolean

  /**
   * Extends the checkbox with a description
   */
  description?: string

  labelClass?: string
}

const props = withDefaults(defineProps<CheckboxProps>(), {
  disabled: false,
  hideLabel: false,
  indeterminate: false,
  modelValue: false,
  nativeValue: undefined,
  required: false,
  dataTestid: 'sb-checkbox',
})

const emit = defineEmits<{
  /**
   * update:modelValue event.
   *
   * @event update:modelValue
   * @property {boolean} modelValue the updated modelValue of the checkbox
   */
  (e: 'update:modelValue', value: boolean): void
  (e: 'blur', event: FocusEvent): void
}>()

const componentClasses = computed(() => [
  useAttrs().class,
  props.disabled && 'sb-checkbox--disabled',
])

const uid = useId()

function handleClick(event: Event): void {
  if (props.disabled) {
    event.preventDefault()
    event.stopPropagation()
    return
  }

  emit('update:modelValue', !props.modelValue)
}

function handleBlur(e: FocusEvent): void {
  emit('blur', e)
}
</script>
